import { useState, useRef, useEffect } from 'react';
import { Upload, FileSpreadsheet, CheckCircle2, CheckCircle, AlertCircle, Loader, Users, RefreshCw, Info, Sparkles, UserX, Search, Download, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DataImportPage() {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [detectedCols, setDetectedCols] = useState([]);
  const fileInputRef = useRef(null);

  // New Excel expected columns (key ones)
  const KEY_COLS = [
    'Número Personal', 'Correo Empresa', 'Nombre', 'Apellidos',
    'Fecha antigüedad', 'Código contrato', 'Cod Flex Office',
    'Cod Tipo Calendario', 'Nombre Apellidos Evaluador 6',
  ];

  const readExcelHeaders = (f) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const { read, utils } = require('xlsx') || window.XLSX;
        const wb = read(e.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = utils.sheet_to_json(ws, { header: 1 });
        if (rows[0]) setDetectedCols(rows[0].map(String));
      } catch {
        setDetectedCols([]);
      }
    };
    reader.readAsArrayBuffer(f);
  };

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.match(/\.(xlsx|xls|csv)$/i)) {
      setError('Solo se aceptan archivos .xlsx, .xls o .csv');
      return;
    }
    setFile(f);
    setError('');
    setResult(null);
    setDetectedCols([]);
    // For Excel files, try to read headers for preview
    if (f.name.match(/\.(xlsx|xls)$/i)) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          // Use SheetJS if available via CDN, otherwise skip preview
          if (window.XLSX) {
            const wb = window.XLSX.read(e.target.result, { type: 'array' });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const rows = window.XLSX.utils.sheet_to_json(ws, { header: 1 });
            if (rows[0]) setDetectedCols(rows[0].map(String).filter(Boolean));
          }
        } catch { setDetectedCols([]); }
      };
      reader.readAsArrayBuffer(f);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const token = localStorage.getItem('valoriza_token');
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_URL}/api/sync/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Error al importar');
      }
      const data = await res.json();
      setResult(data);
    } catch (e) {
      setError(e.message || 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold font-heading flex items-center gap-2">
          <FileSpreadsheet className="h-6 w-6 text-brand" />
          Importar Empleados
        </h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Sube el snapshot diario desde SAP/SFTP para sincronizar empleados
        </p>
      </div>

      {/* Info box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 mb-6 flex gap-3">
        <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300 space-y-2">
          <p className="font-medium">Formatos soportados: CSV (separado por punto y coma) o Excel (.xlsx)</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5">
            <div>
              <p className="text-xs font-semibold mb-1 text-blue-800 dark:text-blue-200">Campos de identidad</p>
              <ul className="text-xs space-y-0.5 list-disc list-inside">
                <li><strong>Correo Empresa</strong> → email de acceso (obligatorio)</li>
                <li><strong>Correo Personal</strong> → email personal (opcional)</li>
                <li><strong>Número Personal</strong> → ID nómina (para LOGA)</li>
                <li><strong>Código contrato</strong> → contrato (para LOGA)</li>
                <li><strong>Nombre / Apellidos</strong> → nombre completo</li>
                <li><strong>DNI</strong> → documento identidad</li>
                <li><strong>Agrupación</strong> → grupo organizativo</li>
              </ul>
            </div>
            <div>
              <p className="text-xs font-semibold mb-1 text-blue-800 dark:text-blue-200">Campos de configuración</p>
              <ul className="text-xs space-y-0.5 list-disc list-inside">
                <li><strong>Fecha antigüedad</strong> → días vacaciones (&lt;1 año prorrateado)</li>
                <li><strong>Fecha Inicio / Fecha Fin</strong> → alta/baja</li>
                <li><strong>Cod Flex Office</strong> → activa/desactiva teletrabajo</li>
                <li><strong>Cod Tipo Calendario</strong> → calendario de festivos</li>
                <li><strong>Nombre Apellidos Evaluador 6</strong> → manager directo</li>
                <li><strong>Nombre Apellidos Evaluador 2</strong> → manager N+2</li>
                <li><strong>Desc Puesto / Desc Funcion / Desc Rol</strong> → cargo</li>
              </ul>
            </div>
          </div>
          <p className="text-xs mt-1 font-medium text-blue-800 dark:text-blue-200">Los roles y contraseñas existentes no se sobreescriben. El sistema detecta las columnas automáticamente.</p>
        </div>
      </div>

      {/* Drop zone */}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => fileInputRef.current?.click()}
        className={`relative cursor-pointer rounded-2xl border-2 border-dashed p-10 text-center transition-all ${
          dragOver ? 'border-brand bg-brand/5 scale-[1.01]' :
          file ? 'border-green-400 bg-green-50 dark:bg-green-900/20' :
          'border-border hover:border-brand/50 hover:bg-muted/30'
        }`}
        data-testid="import-drop-zone"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          className="hidden"
          onChange={(e) => handleFile(e.target.files[0])}
          data-testid="import-file-input"
        />
        {file ? (
          <>
            <FileSpreadsheet className="h-10 w-10 mx-auto mb-3 text-green-500" />
            <p className="font-semibold text-sm">{file.name}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {(file.size / 1024).toFixed(0)} KB — Haz clic para cambiar
            </p>
          </>
        ) : (
          <>
            <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground/40" />
            <p className="font-semibold text-sm">Arrastra aquí el archivo de empleados</p>
            <p className="text-xs text-muted-foreground mt-1">o haz clic para seleccionar (.xlsx, .xls, .csv)</p>
          </>
        )}
      </div>

      {/* Column detection preview */}
      {detectedCols.length > 0 && (
        <div className="rounded-xl border border-border bg-muted/20 p-3 space-y-2">
          <p className="text-xs font-semibold flex items-center gap-1.5">
            <CheckCircle className="h-3.5 w-3.5 text-green-500" />
            {detectedCols.length} columnas detectadas en el Excel
          </p>
          <div className="flex flex-wrap gap-1.5">
            {KEY_COLS.map(kc => {
              const found = detectedCols.some(c => c.toLowerCase().includes(kc.toLowerCase().replace('ú','u').replace('ó','o').replace('é','e').replace('á','a').replace('ñ','n')));
              return (
                <span
                  key={kc}
                  className={`text-xs px-2 py-0.5 rounded-full border font-medium ${
                    found
                      ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800'
                      : 'bg-orange-50 text-orange-600 border-orange-200 dark:bg-orange-900/10 dark:text-orange-400 dark:border-orange-800'
                  }`}
                  title={found ? 'Detectado' : 'No encontrado en el archivo'}
                >
                  {found ? '✓' : '!'} {kc}
                </span>
              );
            })}
          </div>
          {KEY_COLS.some(kc => !detectedCols.some(c => c.toLowerCase().includes(kc.toLowerCase().replace('ú','u').replace('ó','o').replace('é','e').replace('á','a').replace('ñ','n')))) && (
            <p className="text-xs text-orange-600 dark:text-orange-400">
              Las columnas marcadas con ! no se encontraron — esas funcionalidades no se actualizarán para este archivo.
            </p>
          )}
        </div>
      )}

      {error && (
        <div className="mt-3 flex items-center gap-2 p-3 bg-destructive/10 rounded-lg text-sm text-destructive border border-destructive/20">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <Button
        onClick={handleImport}
        disabled={!file || loading}
        data-testid="btn-import-confirm"
        className="w-full mt-4 h-11 bg-brand hover:bg-brand-hover text-white font-semibold gap-2"
      >
        {loading ? (
          <><Loader className="h-4 w-4 animate-spin" /> Importando...</>
        ) : (
          <><RefreshCw className="h-4 w-4" /> Importar y sincronizar</>
        )}
      </Button>

      {/* Result */}
      {result && (
        <div className="mt-6 bg-card border border-border rounded-2xl overflow-hidden shadow-sm" data-testid="import-result">
          <div className="px-5 py-3 bg-green-50 dark:bg-green-900/20 border-b border-border flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-500" />
            <span className="font-semibold text-sm text-green-700 dark:text-green-300">
              Importación completada
            </span>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold font-heading text-brand">{result.created}</div>
                <div className="text-xs text-muted-foreground mt-1">Nuevos</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold font-heading text-blue-600">{result.updated}</div>
                <div className="text-xs text-muted-foreground mt-1">Actualizados</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold font-heading text-orange-500">{result.deactivated ?? 0}</div>
                <div className="text-xs text-muted-foreground mt-1">Dados de baja</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold font-heading text-muted-foreground">{result.skipped}</div>
                <div className="text-xs text-muted-foreground mt-1">Sin email (omitidos)</div>
              </div>
            </div>
            <div className="bg-muted/40 rounded-lg px-4 py-2.5 flex items-center gap-2 text-sm">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Total procesados:</span>
              <span className="font-semibold">{result.total_processed} filas</span>
            </div>
            {result.errors?.length > 0 && (
              <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <p className="text-xs font-semibold text-yellow-700 dark:text-yellow-300 mb-1">
                  Avisos ({result.errors.length}):
                </p>
                {result.errors.map((e, i) => (
                  <p key={`err-${i}`} className="text-xs text-yellow-600">{e}</p>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Section: Directory enrichment */}
      <BulkEnrichSection />
      <DirectoryEnrichSection />
      <ContactInfoEnrichSection />
      <DirectoryEmbedShareSection />
    </div>
  );
}

// ─── Directory enrichment (manual CSV upload for tipo_calendario + fecha_fin) ─────
function DirectoryEnrichSection() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const run = async (mode) => {
    if (!file) { setError('Selecciona el CSV de Integración Solicitud de Vacaciones'); return; }
    setLoading(true); setError(''); setResult(null);
    if (mode === 'preview') setPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('valoriza_token');
      const res = await fetch(`${API_URL}/api/admin/directory-enrich/${mode}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error procesando el archivo');
      if (mode === 'preview') setPreview(data); else setResult(data);
    } catch (e) {
      setError(e.message || 'Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-12 pt-8 border-t border-border" data-testid="directory-enrich-section">
      <div className="mb-4">
        <h2 className="text-lg font-bold font-heading flex items-center gap-2">
          <Users className="h-5 w-5 text-brand" />
          Enriquecer Directorio
        </h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          Importa el CSV <em>INTEGRACIONES_SOLICITUD_DE_VACACIONES</em> para añadir
          <strong> Tipo de Calendario</strong> y <strong>Fecha Fin de Contrato</strong> a las personas existentes.
          No crea ni desactiva usuarios.
        </p>
      </div>

      <div className="rounded-xl border border-dashed border-border bg-card p-4 space-y-3">
        <input
          ref={ref}
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setResult(null); setError(''); }}
          className="hidden"
          data-testid="enrich-file-input"
        />
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" onClick={() => ref.current?.click()} data-testid="enrich-pick-btn">
            <Upload className="h-4 w-4 mr-1.5" />
            {file ? 'Cambiar archivo' : 'Seleccionar CSV'}
          </Button>
          {file && (
            <span className="text-sm text-muted-foreground truncate flex-1" data-testid="enrich-filename">
              <FileSpreadsheet className="h-3.5 w-3.5 inline mr-1" />
              {file.name} · {(file.size / 1024).toFixed(0)} KB
            </span>
          )}
        </div>

        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() => run('preview')}
            disabled={!file || loading}
            data-testid="enrich-preview-btn"
          >
            {loading ? <Loader className="h-4 w-4 animate-spin mr-1.5" /> : <Info className="h-4 w-4 mr-1.5" />}
            Vista previa
          </Button>
          <Button
            onClick={() => run('import')}
            disabled={!file || loading}
            data-testid="enrich-import-btn"
          >
            {loading ? <Loader className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle className="h-4 w-4 mr-1.5" />}
            Importar
          </Button>
        </div>

        {error && (
          <div className="text-sm text-destructive flex items-start gap-2" data-testid="enrich-error">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {preview && (
          <div className="rounded-lg bg-blue-50 dark:bg-blue-900/15 border border-blue-200 dark:border-blue-900/30 p-3 text-sm space-y-2" data-testid="enrich-preview">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
              <div><div className="text-2xl font-bold font-heading">{preview.total_rows}</div><div className="text-xs text-muted-foreground">Filas totales</div></div>
              <div><div className="text-2xl font-bold font-heading">{preview.rows_with_data}</div><div className="text-xs text-muted-foreground">Con datos nuevos</div></div>
              <div><div className="text-2xl font-bold font-heading text-emerald-600">{preview.would_update}</div><div className="text-xs text-muted-foreground">Se actualizarán</div></div>
              <div><div className="text-2xl font-bold font-heading text-amber-600">{preview.would_skip_no_match}</div><div className="text-xs text-muted-foreground">Sin coincidencia</div></div>
            </div>
            {preview.sample_changes?.length > 0 && (
              <div className="mt-2">
                <p className="text-xs font-semibold mb-1">Muestra de cambios:</p>
                <ul className="text-xs space-y-0.5">
                  {preview.sample_changes.map((c, i) => (
                    <li key={i}>
                      • <strong>{c.name}</strong> ({c.email}) → calendario: <code className="bg-card px-1 rounded">{c.tipo_calendario_desc || '—'}</code> · fin: <code className="bg-card px-1 rounded">{c.fecha_fin || '—'}</code>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/15 border border-emerald-200 dark:border-emerald-900/30 p-3 text-sm" data-testid="enrich-result">
            <div className="flex items-center gap-2 mb-2 font-semibold text-emerald-700 dark:text-emerald-300">
              <CheckCircle2 className="h-4 w-4" /> Importación completada
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
              <div><div className="text-2xl font-bold font-heading text-emerald-600">{result.updated}</div><div className="text-xs text-muted-foreground">Actualizadas</div></div>
              <div><div className="text-2xl font-bold font-heading text-muted-foreground">{result.no_change}</div><div className="text-xs text-muted-foreground">Sin cambios</div></div>
              <div><div className="text-2xl font-bold font-heading text-amber-600">{result.no_match}</div><div className="text-xs text-muted-foreground">Sin coincidencia</div></div>
              <div><div className="text-2xl font-bold font-heading">{result.total_rows}</div><div className="text-xs text-muted-foreground">Filas totales</div></div>
            </div>
            {result.error_count > 0 && (
              <p className="text-xs text-amber-600 mt-2">{result.error_count} filas con errores menores (ver logs).</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Contact info enrichment (centro de trabajo + teléfonos + ubicación) ─────
function ContactInfoEnrichSection() {
  const [centroFile, setCentroFile] = useState(null);
  const [entraFile, setEntraFile] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const centroRef = useRef(null);
  const entraRef = useRef(null);

  const run = async () => {
    if (!centroFile && !entraFile) {
      setError('Sube al menos uno de los dos archivos.');
      return;
    }
    setLoading(true); setError(''); setResult(null);
    try {
      const fd = new FormData();
      if (centroFile) fd.append('centro_csv', centroFile);
      if (entraFile) fd.append('entra_xlsx', entraFile);
      const token = localStorage.getItem('valoriza_token');
      const res = await fetch(`${API_URL}/api/admin/directory-enrich/import-contact-info`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error procesando los archivos');
      setResult(data);
    } catch (e) {
      setError(e.message || 'Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-12 pt-8 border-t border-border" data-testid="contact-info-enrich-section">
      <div className="mb-4">
        <h2 className="text-lg font-bold font-heading flex items-center gap-2">
          <Users className="h-5 w-5 text-brand" />
          Contacto y centro de trabajo
        </h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          Añade <strong>teléfonos, móviles, ubicación de oficina</strong> (export Entra ID)
          y <strong>centro de trabajo</strong> (CSV SCOUTD_VSM_PERSONAL_MEDIOAMBIENTE).
          Puedes subir uno o ambos a la vez.
        </p>
      </div>

      <div className="rounded-xl border border-dashed border-border bg-card p-4 space-y-3">
        <input
          ref={centroRef}
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => { setCentroFile(e.target.files?.[0] || null); setResult(null); setError(''); }}
          className="hidden"
          data-testid="centro-file-input"
        />
        <input
          ref={entraRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={(e) => { setEntraFile(e.target.files?.[0] || null); setResult(null); setError(''); }}
          className="hidden"
          data-testid="entra-file-input"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-lg border border-border p-3">
            <div className="text-xs font-semibold mb-1">Centro de trabajo (CSV)</div>
            <Button variant="outline" size="sm" onClick={() => centroRef.current?.click()} data-testid="centro-pick-btn">
              <Upload className="h-3.5 w-3.5 mr-1.5" />
              {centroFile ? 'Cambiar' : 'Seleccionar SCOUTD_VSM_PERSONAL…'}
            </Button>
            {centroFile && (
              <div className="text-xs text-muted-foreground mt-2 truncate" data-testid="centro-filename">
                <FileSpreadsheet className="h-3 w-3 inline mr-0.5" /> {centroFile.name}
              </div>
            )}
          </div>
          <div className="rounded-lg border border-border p-3">
            <div className="text-xs font-semibold mb-1">Entra ID export (XLSX)</div>
            <Button variant="outline" size="sm" onClick={() => entraRef.current?.click()} data-testid="entra-pick-btn">
              <Upload className="h-3.5 w-3.5 mr-1.5" />
              {entraFile ? 'Cambiar' : 'Seleccionar exportUsers_…'}
            </Button>
            {entraFile && (
              <div className="text-xs text-muted-foreground mt-2 truncate" data-testid="entra-filename">
                <FileSpreadsheet className="h-3 w-3 inline mr-0.5" /> {entraFile.name}
              </div>
            )}
          </div>
        </div>

        <Button
          onClick={run}
          disabled={(!centroFile && !entraFile) || loading}
          data-testid="contact-import-btn"
          className="w-full"
        >
          {loading ? <Loader className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle className="h-4 w-4 mr-1.5" />}
          Importar contacto y centros
        </Button>

        {error && (
          <div className="text-sm text-destructive flex items-start gap-2" data-testid="contact-error">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {result && (
          <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/15 border border-emerald-200 dark:border-emerald-900/30 p-3 text-sm" data-testid="contact-result">
            <div className="flex items-center gap-2 mb-2 font-semibold text-emerald-700 dark:text-emerald-300">
              <CheckCircle2 className="h-4 w-4" /> Importación completada
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 text-center">
              <div><div className="text-2xl font-bold font-heading text-emerald-600">{result.updated}</div><div className="text-xs text-muted-foreground">Actualizadas</div></div>
              <div><div className="text-2xl font-bold font-heading">{result.centro_hits}</div><div className="text-xs text-muted-foreground">Centro detectado</div></div>
              <div><div className="text-2xl font-bold font-heading">{result.entra_hits_email}</div><div className="text-xs text-muted-foreground">Entra (email)</div></div>
              <div><div className="text-2xl font-bold font-heading">{result.entra_hits_name}</div><div className="text-xs text-muted-foreground">Entra (nombre)</div></div>
              <div><div className="text-2xl font-bold font-heading text-muted-foreground">{result.total_active_users}</div><div className="text-xs text-muted-foreground">Activos en BD</div></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Embed share (public directory iframe for intranet) ─────────────────────
function DirectoryEmbedShareSection() {
  const [embed, setEmbed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('valoriza_token');
    fetch(`${API_URL}/api/directory/admin/embed-key`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(setEmbed)
      .catch(() => setEmbed(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!embed?.key) {
    return (
      <div className="mt-12 pt-8 border-t border-border">
        <h2 className="text-lg font-bold font-heading flex items-center gap-2">
          <Users className="h-5 w-5 text-brand" />
          Compartir directorio (embed)
        </h2>
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/15 dark:border-amber-900/30 p-3 text-sm">
          <AlertCircle className="h-4 w-4 inline mr-1.5 text-amber-600" />
          La variable de entorno <code className="bg-card px-1 rounded">DIRECTORY_EMBED_KEY</code> no está configurada en este entorno.
          Añádela al <code>.env</code> del backend y reinicia para habilitar el embed.
        </div>
      </div>
    );
  }

  const origin = window.location.origin;
  const url = `${origin}/directory-embed?key=${embed.key}`;
  const iframe = `<iframe
  src="${url}"
  title="Directorio Valoriza"
  style="width:100%; height:900px; border:0; border-radius:12px;"
  loading="lazy"
  allow="clipboard-write">
</iframe>`;

  const copy = async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setTimeout(() => setCopied(''), 1500);
    } catch {
      setCopied('error');
    }
  };

  return (
    <div className="mt-12 pt-8 border-t border-border" data-testid="embed-share-section">
      <div className="mb-3">
        <h2 className="text-lg font-bold font-heading flex items-center gap-2">
          <Users className="h-5 w-5 text-brand" />
          Compartir directorio en la intranet
        </h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          Copia la URL o el HTML del iframe y dáselo al equipo que mantiene la intranet corporativa.
        </p>
      </div>

      <div className="space-y-3">
        {/* Key */}
        <div className="rounded-lg border border-border bg-card p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Clave actual</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => copy(embed.key, 'key')}
              data-testid="copy-embed-key"
            >
              {copied === 'key' ? <><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Copiado</> : 'Copiar clave'}
            </Button>
          </div>
          <code className="block bg-muted/40 px-3 py-2 rounded text-xs break-all font-mono">{embed.key}</code>
          <p className="text-xs text-muted-foreground">
            Para <strong>rotar la clave</strong>: cambia <code className="bg-muted/40 px-1 rounded">DIRECTORY_EMBED_KEY</code> en el <code>.env</code> de producción y redespliega.
          </p>
        </div>

        {/* URL */}
        <div className="rounded-lg border border-border bg-card p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">URL pública</span>
            <div className="flex gap-2">
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="text-xs px-2 py-1 rounded border border-border hover:bg-muted"
                data-testid="open-embed-url"
              >
                Probar
              </a>
              <Button variant="outline" size="sm" onClick={() => copy(url, 'url')} data-testid="copy-embed-url">
                {copied === 'url' ? <><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Copiado</> : 'Copiar URL'}
              </Button>
            </div>
          </div>
          <code className="block bg-muted/40 px-3 py-2 rounded text-xs break-all font-mono">{url}</code>
        </div>

        {/* HTML iframe */}
        <div className="rounded-lg border border-border bg-card p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">HTML iframe (para la intranet)</span>
            <Button variant="outline" size="sm" onClick={() => copy(iframe, 'iframe')} data-testid="copy-embed-iframe">
              {copied === 'iframe' ? <><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Copiado</> : 'Copiar HTML'}
            </Button>
          </div>
          <pre className="bg-muted/40 px-3 py-2 rounded text-xs overflow-x-auto font-mono">{iframe}</pre>
        </div>

        <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-900/15 dark:border-blue-900/30 p-3 text-xs text-blue-800 dark:text-blue-300 space-y-1">
          <p><strong>Qué se expone:</strong> nombre, email corporativo, puesto, área funcional, modalidad, calendario, jerarquía (N+1/N+2), centro de trabajo, teléfono cuando esté disponible y foto.</p>
          <p><strong>Qué NO se expone:</strong> DNI, correo personal, agrupaciones internas, fechas de antigüedad.</p>
          <p><strong>Login en la intranet:</strong> no requiere. El acceso se controla con la clave secreta del embed.</p>
        </div>
      </div>
    </div>
  );
}

// ─── Mega bulk enrich (5 CSVs en un click) ──────────────────────────────────
function BulkEnrichSection() {
  const files = useRef({});
  const [selected, setSelected] = useState({});
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // "Sin puesto" audit panel
  const [missing, setMissing] = useState(null);
  const [missingLoading, setMissingLoading] = useState(false);
  const [missingError, setMissingError] = useState('');
  const [missingFilter, setMissingFilter] = useState('');

  // Upload SAP CB master
  const [cbBusy, setCbBusy] = useState(false);
  const [cbResult, setCbResult] = useState(null);
  const [cbError, setCbError] = useState('');
  const cbFileRef = useRef(null);

  const uploadSapCb = async (file) => {
    setCbBusy(true); setCbError(''); setCbResult(null);
    try {
      const fd = new FormData();
      fd.append('cb_csv', file);
      const res = await fetch(`${API_URL}/api/admin/directory-enrich/sap-cb-master`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('valoriza_token')}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setCbResult(data);
    } catch (e) {
      setCbError(e.message || 'Error al subir el master');
    } finally {
      setCbBusy(false);
    }
  };

  const loadMissingPuesto = async () => {
    setMissingLoading(true);
    setMissingError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/directory-enrich/missing-puesto`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('valoriza_token')}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setMissing(data);
    } catch (e) {
      setMissingError(e.message || 'Error cargando la lista');
    } finally {
      setMissingLoading(false);
    }
  };

  const downloadMissingCsv = () => {
    if (!missing?.items?.length) return;
    const rows = [
      ['email', 'first_name', 'last_name', 'numero_personal', 'dni', 'manager_nombre', 'centro_trabajo_desc', 'centro_coste_desc', 'funcion', 'hint'],
      ...missing.items.map(i => [i.email, i.first_name, i.last_name, i.numero_personal || '', i.dni || '', i.manager_nombre || '', i.centro_trabajo_desc || '', i.centro_coste_desc || '', i.funcion || '', i.hint]),
    ];
    const csv = rows.map(r => r.map(c => `"${(c || '').toString().replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    a.href = url;
    a.download = `usuarios-sin-puesto-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const FIELDS = [
    { key: 'vac_csv',       label: 'Vacaciones (INTEGRACIONES_SOLICITUD_DE_VACACIONES.csv)', accept: '.csv', desc: 'Puesto, función, rol, agrupación, calendario, fechas' },
    { key: 'onb_csv',       label: 'Onboarding (Integracion_Onboarding.csv)',                 accept: '.csv', desc: 'Organización I/II, centro de coste' },
    { key: 'ma_csv',        label: 'Medio Ambiente (VSM_PERSONAL_MEDIOAMBIENTE.csv)',         accept: '.csv', desc: 'Centro de trabajo' },
    { key: 'marketing_csv', label: 'LOGA Marketing (INFORME_LOGA_EMPLEADOS_ID_MARKETING.csv)', accept: '.csv', desc: 'Teléfono / móvil corporativo' },
    { key: 'entra_xlsx',    label: 'Entra ID (exportUsers_*.xlsx)',                            accept: '.xlsx,.xls', desc: 'Teléfono, móvil y ubicación física' },
  ];

  const pick = (key, accept) => {
    if (!files.current[key]) {
      const i = document.createElement('input');
      i.type = 'file'; i.accept = accept;
      i.onchange = (e) => {
        const f = e.target.files?.[0]; if (!f) return;
        setSelected(prev => ({ ...prev, [key]: f }));
      };
      files.current[key] = i;
    }
    files.current[key].click();
  };

  const run = async () => {
    if (Object.keys(selected).length === 0) { setError('Selecciona al menos un archivo'); return; }
    setRunning(true); setError(''); setResult(null);
    try {
      const fd = new FormData();
      for (const k of Object.keys(selected)) fd.append(k, selected[k]);
      const token = localStorage.getItem('valoriza_token');
      const res = await fetch(`${API_URL}/api/admin/directory-enrich/bulk`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      // Defensive: if the proxy returns HTML (504/524/etc), don't try to parse JSON
      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        const text = await res.text();
        const preview = text.slice(0, 200).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
        throw new Error(`Respuesta no JSON (HTTP ${res.status}). El servidor pudo haber tardado demasiado. ${preview || ''}`);
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error procesando los archivos');
      setResult(data);
    } catch (e) {
      setError(e.message || 'Error');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="mt-12 pt-8 border-t border-border" data-testid="bulk-enrich-section">
      <div className="mb-3 flex items-start gap-2">
        <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 text-white flex items-center justify-center flex-shrink-0">
          <Upload className="h-4 w-4" />
        </div>
        <div>
          <h2 className="text-lg font-bold font-heading">Enriquecimiento masivo (5 archivos)</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Sube los archivos que tengas en un solo click. El sistema combina la información y aplica todo a la vez.
            Puedes subir solo algunos — los que falten se ignoran.
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-dashed border-border bg-card p-4 space-y-3">
        {FIELDS.map(f => (
          <div key={f.key} className="flex items-center justify-between gap-3 p-2.5 rounded-lg hover:bg-muted/30 transition-colors">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate" data-testid={`bulk-label-${f.key}`}>{f.label}</div>
              <div className="text-xs text-muted-foreground truncate">{f.desc}</div>
              {selected[f.key] && (
                <div className="text-xs text-emerald-600 mt-0.5 truncate">
                  <FileSpreadsheet className="h-3 w-3 inline mr-1" /> {selected[f.key].name} · {(selected[f.key].size/1024).toFixed(0)} KB
                </div>
              )}
            </div>
            <Button
              variant={selected[f.key] ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => pick(f.key, f.accept)}
              data-testid={`bulk-pick-${f.key}`}
            >
              {selected[f.key] ? 'Cambiar' : 'Elegir'}
            </Button>
          </div>
        ))}

        <Button
          onClick={run}
          disabled={running || Object.keys(selected).length === 0}
          className="w-full mt-2"
          data-testid="bulk-run-btn"
        >
          {running
            ? <><Loader className="h-4 w-4 animate-spin mr-1.5" /> Procesando…</>
            : <><Sparkles className="h-4 w-4 mr-1.5" /> Procesar todo ({Object.keys(selected).length} archivo{Object.keys(selected).length !== 1 ? 's' : ''})</>}
        </Button>

        {error && (
          <div className="text-sm text-destructive flex items-start gap-2" data-testid="bulk-error">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {result && (
          <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/15 border border-emerald-200 dark:border-emerald-900/30 p-4 text-sm space-y-3" data-testid="bulk-result">
            <div className="flex items-center gap-2 font-semibold text-emerald-700 dark:text-emerald-300">
              <CheckCircle2 className="h-4 w-4" /> Enriquecimiento completado
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-center">
              <KPI label="Usuarios actualizados" value={result.users_updated} />
              <KPI label="Función rellenada" value={result.funcion_backfilled} />
              <KPI label="Activos totales" value={result.total_active} />
            </div>
            {result.per_file && (
              <div className="text-xs space-y-0.5">
                <p className="font-semibold uppercase tracking-wider mb-1">Hits por archivo:</p>
                {Object.entries(result.per_file).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-emerald-200/30 dark:border-emerald-900/30 py-0.5">
                    <span>{k}</span>
                    <span><strong>{v.hits}</strong>{v.rows ? <span className="text-muted-foreground"> / {v.rows} filas</span> : null}</span>
                  </div>
                ))}
              </div>
            )}
            {result.coverage && (
              <div className="text-xs space-y-0.5">
                <p className="font-semibold uppercase tracking-wider mb-1">Cobertura tras enriquecer:</p>
                {Object.entries(result.coverage).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-emerald-200/30 dark:border-emerald-900/30 py-0.5">
                    <span className="capitalize">{k.replace(/_/g, ' ')}</span>
                    <span><strong>{v.count}</strong> <span className="text-emerald-600">({v.pct}%)</span></span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* SAP CB master upload */}
        <div className="rounded-2xl border border-border bg-card p-5 space-y-4" data-testid="sap-cb-master-panel">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400 flex items-center justify-center flex-shrink-0">
              <Building2 className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold font-heading">Master oficial Ubicaciones SAP (CB)</h3>
              <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
                Sube el Excel oficial de Centros de Beneficio de SAP (columnas <code>Cebe</code> y <code>Descripción</code>; también acepta el CSV antiguo con <code>id_centro_beneficio</code>/<code>nombre_centro_beneficio</code>). El sistema cruza por el código de 7 caracteres del centro de coste de cada empleado (numérico o con letra, ej. Z504708).
              </p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                ref={cbFileRef}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadSapCb(f); }}
                className="hidden"
                data-testid="sap-cb-input"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => cbFileRef.current?.click()}
                disabled={cbBusy}
                data-testid="btn-upload-sap-cb"
                className="gap-2"
              >
                {cbBusy ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                Subir master CB
              </Button>
            </div>
          </div>
          {cbError && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5" /> {cbError}
            </div>
          )}
          {cbResult && (
            <div className="rounded-lg border border-green-200 bg-green-50 dark:border-green-900/30 dark:bg-green-900/10 p-3 text-sm space-y-1" data-testid="sap-cb-result">
              <div className="flex items-center gap-2 font-medium text-green-700 dark:text-green-300">
                <CheckCircle2 className="h-4 w-4" /> Master aplicado correctamente
              </div>
              <div className="text-xs text-green-800 dark:text-green-200 grid grid-cols-3 gap-2 pt-1">
                <div><span className="font-semibold">{cbResult.master_count}</span> centros en master</div>
                <div><span className="font-semibold">{cbResult.users_updated}</span> usuarios actualizados</div>
                <div><span className="font-semibold">{cbResult.users_no_match}</span> sin match (estructura)</div>
              </div>
              {cbResult.duplicate_codes_found > 0 && (
                <div className="text-xs text-amber-700 dark:text-amber-300 pt-1" data-testid="sap-cb-duplicates">
                  ⚠ {cbResult.duplicate_codes_found} código(s) Cebe duplicado(s) en el fichero con descripciones distintas — se mantuvo la primera aparición de cada uno.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Audit panel: usuarios sin puesto */}
        <div className="rounded-2xl border border-border bg-card p-5 space-y-4" data-testid="missing-puesto-panel">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center flex-shrink-0">
                <UserX className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold font-heading flex items-center gap-2">Usuarios sin puesto</h3>
                <p className="text-xs text-muted-foreground mt-0.5 max-w-xl">
                  Lista de personas activas a las que el sync no les pudo asignar un puesto, con una pista sobre por qué falló.
                </p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={loadMissingPuesto}
                disabled={missingLoading}
                data-testid="btn-load-missing-puesto"
                className="gap-2"
              >
                {missingLoading ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
                {missing ? 'Refrescar' : 'Buscar usuarios sin puesto'}
              </Button>
              {missing && missing.count > 0 && (
                <Button variant="outline" size="sm" onClick={downloadMissingCsv} data-testid="btn-export-missing-csv" className="gap-2">
                  <Download className="h-3.5 w-3.5" /> Exportar CSV
                </Button>
              )}
            </div>
          </div>

          {missingError && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5" /> {missingError}
            </div>
          )}

          {missing && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="missing-puesto-stats">
                <KPI label="Total sin puesto" value={missing.count} />
                <KPI label="Sin Nº Personal" value={missing.no_pnr} hideIfZero />
                <KPI label="Email no corporativo" value={missing.non_corporate_email} hideIfZero />
                <KPI label="Sin nombre completo" value={missing.no_name} hideIfZero />
              </div>

              {missing.count === 0 ? (
                <div className="rounded-xl border border-green-200 bg-green-50 dark:border-green-900/30 dark:bg-green-900/10 p-4 text-sm text-green-700 dark:text-green-300 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" /> ¡Cobertura total! Todos los usuarios activos tienen puesto asignado.
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    placeholder="Filtrar por nombre, email o pista…"
                    value={missingFilter}
                    onChange={(e) => setMissingFilter(e.target.value)}
                    data-testid="missing-filter-input"
                    className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-brand/30"
                  />
                  <div className="max-h-[420px] overflow-y-auto rounded-xl border border-border" data-testid="missing-list">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50 sticky top-0">
                        <tr className="text-left">
                          <th className="px-3 py-2 font-semibold">Nombre</th>
                          <th className="px-3 py-2 font-semibold">Email</th>
                          <th className="px-3 py-2 font-semibold">Nº Personal</th>
                          <th className="px-3 py-2 font-semibold">Pista</th>
                        </tr>
                      </thead>
                      <tbody>
                        {missing.items
                          .filter((i) => {
                            if (!missingFilter.trim()) return true;
                            const q = missingFilter.toLowerCase();
                            return (
                              (i.full_name || '').toLowerCase().includes(q) ||
                              (i.email || '').toLowerCase().includes(q) ||
                              (i.hint || '').toLowerCase().includes(q)
                            );
                          })
                          .map((i) => (
                            <tr key={i.id} className="border-t border-border/60 hover:bg-muted/30" data-testid={`missing-row-${i.email}`}>
                              <td className="px-3 py-2 font-medium">{i.full_name || '—'}</td>
                              <td className="px-3 py-2 text-muted-foreground">{i.email || '—'}</td>
                              <td className="px-3 py-2 font-mono text-muted-foreground">{i.numero_personal || <span className="text-amber-600">∅</span>}</td>
                              <td className="px-3 py-2 text-muted-foreground">{i.hint}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, hideIfZero }) {
  if (hideIfZero && !value) return null;
  return (
    <div>
      <div className="text-2xl font-bold font-heading text-emerald-600">{value ?? 0}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}
